import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { StatusChip } from './ui';
import { toast } from 'react-toastify';

export default function WorkOrderDrawer({
  isOpen,
  onClose,
  workOrder,
  onRefresh,
}) {
  const [productionLogs, setProductionLogs] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [productionQuantity, setProductionQuantity] = useState('');
  const [isLoggingProduction, setIsLoggingProduction] = useState(false);
  const [localWorkOrder, setLocalWorkOrder] = useState(null);

  // Production log form fields
  const [shops, setShops] = useState([]);
  const [machines, setMachines] = useState([]);
  const [selectedShop, setSelectedShop] = useState('');
  const [selectedMachine, setSelectedMachine] = useState('');
  const [productionDate, setProductionDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  // Keep local work order state in sync
  useEffect(() => {
    if (workOrder) {
      setLocalWorkOrder(workOrder);
    }
  }, [workOrder]);

  useEffect(() => {
    if (isOpen && workOrder) {
      fetchWorkOrderDetails();
    }
  }, [isOpen, workOrder]);

  // Fetch shops and machines for dropdowns
  useEffect(() => {
    const fetchShopsAndMachines = async () => {
      const [shopsRes, machinesRes] = await Promise.all([
        supabase.from('shops').select('id, name').order('name'),
        supabase.from('machines').select('id, name, shop_id').order('name'),
      ]);
      if (shopsRes.data) setShops(shopsRes.data);
      if (machinesRes.data) setMachines(machinesRes.data);
    };
    fetchShopsAndMachines();
  }, []);

  const fetchWorkOrderDetails = async () => {
    if (!workOrder) return;
    setLoading(true);

    try {
      // Fetch production logs for this work order
      const { data: logs, error: logsError } = await supabase
        .from('production_logs')
        .select('log_id, quantity_produced, production_date, shops(name), machines(name)')
        .eq('job_id', workOrder.id)
        .order('production_date', { ascending: false });

      if (logsError) throw logsError;
      setProductionLogs(logs || []);

      // Fetch deliveries linked to this work order
      const { data: dels, error: delsError } = await supabase
        .from('deliveries')
        .select('package_number, quantity, ship_date, tracking_number, ship_by')
        .eq('job_id', workOrder.id)
        .order('ship_date', { ascending: false });

      if (delsError) throw delsError;
      setDeliveries(dels || []);
    } catch (err) {
      console.error('Error fetching work order details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogProduction = async () => {
    if (!productionQuantity || productionQuantity <= 0) {
      toast.error('Please enter a valid production quantity.');
      return;
    }

    const qty = parseInt(productionQuantity);
    const newProducedQuantity = localWorkOrder.produced_quantity + qty;

    setIsLoggingProduction(true);
    try {
      // Update work order produced quantity
      // Set status to ready_for_shipment if we've met or exceeded the order quantity
      const newStatus = newProducedQuantity >= localWorkOrder.quantity_on_order ? 'ready_for_shipment' : localWorkOrder.status;
      const { error: workOrderError } = await supabase
        .from('jobs')
        .update({
          produced_quantity: newProducedQuantity,
          status: newStatus,
        })
        .eq('id', localWorkOrder.id);
      if (workOrderError) throw new Error(`Failed to update work order: ${workOrderError.message}`);

      // Update kanban job produced quantity
      const { data: kanbanJobData, error: fetchKanbanError } = await supabase
        .from('kanban_jobs')
        .select('produced_quantity')
        .eq('id', localWorkOrder.kanban_job_id)
        .single();
      if (fetchKanbanError) throw new Error(`Failed to fetch Kanban job: ${fetchKanbanError.message}`);

      const newKanbanProducedQuantity = kanbanJobData.produced_quantity + qty;
      const { error: updateKanbanError } = await supabase
        .from('kanban_jobs')
        .update({
          produced_quantity: newKanbanProducedQuantity,
        })
        .eq('id', localWorkOrder.kanban_job_id);
      if (updateKanbanError) throw new Error(`Failed to update Kanban job: ${updateKanbanError.message}`);

      // Log to production_logs table
      const { error: logError } = await supabase
        .from('production_logs')
        .insert({
          job_id: localWorkOrder.id,
          part_number: localWorkOrder.kanban_jobs?.part_number,
          quantity_produced: qty,
          production_date: productionDate,
          shop_id: selectedShop ? parseInt(selectedShop) : null,
          machine_id: selectedMachine ? parseInt(selectedMachine) : null,
        });
      if (logError) console.error('Failed to create production log:', logError);

      // Update local state
      setLocalWorkOrder(prev => ({
        ...prev,
        produced_quantity: newProducedQuantity,
        status: newProducedQuantity >= prev.quantity_on_order ? 'ready_for_shipment' : prev.status,
      }));

      // Clear inputs and refresh
      setProductionQuantity('');
      setSelectedShop('');
      setSelectedMachine('');
      fetchWorkOrderDetails(); // Refresh logs
      onRefresh?.(); // Refresh parent data

      toast.success(`Logged ${qty} units produced!`);
    } catch (err) {
      console.error('Error logging production:', err);
      toast.error(`Failed to log production: ${err.message}`);
    } finally {
      setIsLoggingProduction(false);
    }
  };

  const handleDeleteProductionLog = async (log) => {
    if (!window.confirm(`Delete production log of ${log.quantity_produced} units?`)) return;

    try {
      // Delete the production log
      const { error: deleteError } = await supabase
        .from('production_logs')
        .delete()
        .eq('log_id', log.log_id);
      if (deleteError) throw new Error(`Failed to delete log: ${deleteError.message}`);

      // Update work order produced quantity
      const newProducedQuantity = Math.max(0, localWorkOrder.produced_quantity - log.quantity_produced);
      const { error: workOrderError } = await supabase
        .from('jobs')
        .update({
          produced_quantity: newProducedQuantity,
          status: newProducedQuantity >= localWorkOrder.quantity_on_order ? 'ready_for_shipment' : 'in_production',
        })
        .eq('id', localWorkOrder.id);
      if (workOrderError) throw new Error(`Failed to update work order: ${workOrderError.message}`);

      // Update kanban job produced quantity
      const { data: kanbanJobData, error: fetchKanbanError } = await supabase
        .from('kanban_jobs')
        .select('produced_quantity')
        .eq('id', localWorkOrder.kanban_job_id)
        .single();
      if (fetchKanbanError) throw new Error(`Failed to fetch Kanban job: ${fetchKanbanError.message}`);

      const newKanbanProducedQuantity = Math.max(0, kanbanJobData.produced_quantity - log.quantity_produced);
      const { error: updateKanbanError } = await supabase
        .from('kanban_jobs')
        .update({
          produced_quantity: newKanbanProducedQuantity,
        })
        .eq('id', localWorkOrder.kanban_job_id);
      if (updateKanbanError) throw new Error(`Failed to update Kanban job: ${updateKanbanError.message}`);

      // Update local state
      setLocalWorkOrder(prev => ({
        ...prev,
        produced_quantity: newProducedQuantity,
        status: newProducedQuantity >= prev.quantity_on_order ? 'ready_for_shipment' : 'in_production',
      }));

      // Refresh data
      fetchWorkOrderDetails();
      onRefresh?.();

      toast.success('Production log deleted');
    } catch (err) {
      console.error('Error deleting production log:', err);
      toast.error(`Failed to delete: ${err.message}`);
    }
  };

  // Helper to format dates without timezone shift
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Handle ISO dates by taking just the date part to avoid timezone issues
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  if (!workOrder) return null;

  const wo = localWorkOrder || workOrder;
  const progress = wo.quantity_on_order > 0
    ? Math.round((wo.produced_quantity / wo.quantity_on_order) * 100)
    : 0;
  const remaining = wo.quantity_on_order - wo.produced_quantity;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col bg-white shadow-xl">
                    {/* Header */}
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                      <div className="flex items-center justify-between">
                        <Dialog.Title className="text-base font-semibold text-slate-900">
                          Work Order {wo.po_number}-{wo.po_line_number}
                        </Dialog.Title>
                        <button
                          onClick={onClose}
                          className="p-1 rounded-sm text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto">
                      {/* Summary */}
                      <div className="px-4 py-4 border-b border-slate-100">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-xs text-slate-500">Part</p>
                            <p className="font-mono font-medium text-slate-800">
                              {wo.kanban_jobs?.part_number || wo.part_number}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Status</p>
                            <StatusChip status={wo.status} size="xs" />
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Due Date</p>
                            <p className="font-medium text-slate-800">
                              {formatDate(wo.expected_delivery_date)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500">Quantity</p>
                            <p className="font-medium text-slate-800 tabular-nums">
                              {wo.quantity_on_order}
                            </p>
                          </div>
                          <div className="col-span-2">
                            <p className="text-xs text-slate-500 mb-1">Material</p>
                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs font-medium ${
                              wo.material_status === 'no_stock'
                                ? 'bg-red-100 text-red-700'
                                : wo.material_status === 'ordered'
                                ? 'bg-yellow-100 text-yellow-700'
                                : wo.material_status === 'ready'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-slate-100 text-slate-500'
                            }`}>
                              {wo.material_status === 'no_stock' ? (
                                <>
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                  No Stock
                                </>
                              ) : wo.material_status === 'ordered' ? (
                                <>
                                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-500" />
                                  Ordered
                                </>
                              ) : wo.material_status === 'ready' ? (
                                <>
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                  Ready
                                </>
                              ) : (
                                'Not Set'
                              )}
                            </span>
                            {wo.material_notes && (
                              <p className="text-xs text-slate-500 mt-1 italic">"{wo.material_notes}"</p>
                            )}
                          </div>
                        </div>

                        {/* Progress bar */}
                        <div className="mt-4">
                          <div className="flex justify-between text-xs text-slate-600 mb-1">
                            <span>Production Progress</span>
                            <span className="tabular-nums">{wo.produced_quantity}/{wo.quantity_on_order} ({progress}%)</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-sm overflow-hidden">
                            <div
                              className="h-full bg-slate-600 transition-all duration-300"
                              style={{ width: `${Math.min(progress, 100)}%` }}
                            />
                          </div>
                        </div>

                        {/* Log Production Form */}
                        <div className="mt-4 p-3 bg-slate-50 rounded-sm border border-slate-200">
                          <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                            Log Production
                          </label>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <input
                              type="number"
                              value={productionQuantity}
                              onChange={(e) => setProductionQuantity(e.target.value)}
                              placeholder="Quantity"
                              min="1"
                              className="px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 tabular-nums"
                            />
                            <input
                              type="date"
                              value={productionDate}
                              onChange={(e) => setProductionDate(e.target.value)}
                              className="px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                            />
                            <select
                              value={selectedShop}
                              onChange={(e) => {
                                setSelectedShop(e.target.value);
                                setSelectedMachine(''); // Reset machine when shop changes
                              }}
                              className="px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                            >
                              <option value="">Shop (optional)</option>
                              {shops.map((shop) => (
                                <option key={shop.id} value={shop.id}>{shop.name}</option>
                              ))}
                            </select>
                            <select
                              value={selectedMachine}
                              onChange={(e) => setSelectedMachine(e.target.value)}
                              className="px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                            >
                              <option value="">Machine (optional)</option>
                              {machines
                                .filter((m) => !selectedShop || m.shop_id === parseInt(selectedShop))
                                .map((machine) => (
                                  <option key={machine.id} value={machine.id}>{machine.name}</option>
                                ))}
                            </select>
                          </div>
                          <button
                            onClick={handleLogProduction}
                            disabled={isLoggingProduction || !productionQuantity}
                            className="w-full px-4 py-1.5 text-sm bg-slate-700 text-white rounded-sm hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                          >
                            {isLoggingProduction ? 'Logging...' : 'Log Production'}
                          </button>
                          <p className="text-xs text-slate-500 mt-1.5">
                            {remaining > 0
                              ? `${remaining} remaining on order`
                              : <span className="text-green-600">Order quantity reached ({wo.produced_quantity}/{wo.quantity_on_order})</span>
                            }
                          </p>
                        </div>

                        {/* Link to full page */}
                        <div className="mt-3">
                          <Link
                            to={`/jobs/${wo.po_number}`}
                            className="text-xs text-slate-500 hover:text-slate-700 transition-colors"
                          >
                            View full work order page →
                          </Link>
                        </div>
                      </div>

                      {/* Production Logs */}
                      <div className="px-4 py-3 border-b border-slate-100">
                        <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                          Production History
                        </h3>
                        {loading ? (
                          <p className="text-sm text-slate-500">Loading...</p>
                        ) : productionLogs.length === 0 ? (
                          <p className="text-sm text-slate-500">No production logged yet.</p>
                        ) : (
                          <div className="space-y-2">
                            {productionLogs.map((log) => (
                              <div key={log.log_id} className="flex justify-between items-center text-sm bg-slate-50 px-2 py-1.5 rounded-sm group">
                                <div>
                                  <span className="font-medium text-slate-800 tabular-nums">{log.quantity_produced}</span>
                                  <span className="text-slate-500 ml-2">
                                    {log.shops?.name || '-'} / {log.machines?.name || '-'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-500">
                                    {formatDate(log.production_date)}
                                  </span>
                                  <button
                                    onClick={() => handleDeleteProductionLog(log)}
                                    className="p-1 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Delete log"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Deliveries */}
                      <div className="px-4 py-3">
                        <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                          Deliveries (PKG)
                        </h3>
                        {loading ? (
                          <p className="text-sm text-slate-500">Loading...</p>
                        ) : deliveries.length === 0 ? (
                          <p className="text-sm text-slate-500">No deliveries linked to this work order.</p>
                        ) : (
                          <div className="space-y-2">
                            {deliveries.map((del) => (
                              <div key={del.package_number} className="flex justify-between items-center text-sm bg-slate-50 px-2 py-1.5 rounded-sm">
                                <div>
                                  <span className="font-mono font-medium text-slate-800">{del.package_number}</span>
                                  <span className="text-slate-600 ml-2">×{del.quantity}</span>
                                </div>
                                <div className="text-right text-xs">
                                  <div className="text-slate-700">{del.ship_by}</div>
                                  <div className="text-slate-500">{formatDate(del.ship_date)}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
